import { inject } from "@angular/core";
import { CanActivateChildFn, CanActivateFn, Router } from "@angular/router";
import { STORAGE_KEY } from "app/app-constant";
import { CLIENT_NAVIGATION_FLAT } from "app/client-route";
import { AuthService } from "app/core/auth/auth.service";
import { environment } from "environments/environment";
import { jwtDecode } from "jwt-decode";
import { of, switchMap } from "rxjs";

export interface UserToken {
  id: string;
  userType: string;
  modularPermissions: string[];
  functionalPermissions: string[];
  iat: number; // Issued at (timestamp)
  exp: number; // Expiry time (timestamp)
}

export const PermissionGuard: CanActivateFn | CanActivateChildFn = (
  route,
  state
) => {
  const router: Router = inject(Router);
  const authService = inject(AuthService);

  return authService.check().pipe(
    switchMap((authenticated) => {
      if (!authenticated) {
        // Redirect to login or a default unauthenticated route
        router.navigate(["/sign-in"]);
        return of(false);
      }

      const requiredPermission = route.data?.permission;
      const getTokenPayload = getToken();
      const permissionList = getTokenPayload.modularPermissions; // Fetch user's permissions
      const userType = getTokenPayload.userType; // Fetch user's type

      if (userType === "admin" || userType === "sub-admin") return of(true); // Admins and sub-admins have access to all pages

      if (permissionList && permissionList.includes(requiredPermission)) {
        // User has the required permission, allow access
        return of(true);
      } else {
        // User doesn't have the required permission, find a fallback route
        const redirectURL =
          CLIENT_NAVIGATION_FLAT.find((page) =>
            permissionList?.includes(page.permission)
          )?.link || "/signed-in-redirect"; // Default redirect if no suitable page is found

        router.navigate([redirectURL]);
        return of(false);
      }
    })
  );
};

export function getToken(): UserToken | null {
  const token = localStorage.getItem(STORAGE_KEY.TOKEN);
  if (!token) {
    console.error("No token found in localStorage");
    return null;
  }

  try {
    const options = {
      json: true,
      complete: true,
      header: false,
      algorithm: "RS256",
      key: environment.basicAuthEncryptionKey,
    };

    return jwtDecode<UserToken>(token, options)

  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
}
